import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { ArrowRightOutlined, CheckCircleFilled, HeartFilled } from '@ant-design/icons';

const TEAM = [
  { name: 'Arjun Kapoor',   role: 'Founder & CEO',        avatar: 'AK', color: '#1890ff', bio: 'Former gym owner turned tech entrepreneur. Built Wellness after struggling with manual attendance for 5 years.' },
  { name: 'Sneha Iyer',     role: 'Head of Product',       avatar: 'SI', color: '#52c41a', bio: 'UX designer with 8+ years in health-tech. Passionate about making complex tools simple and beautiful.' },
  { name: 'Rohit Desai',    role: 'Lead Engineer',         avatar: 'RD', color: '#fa8c16', bio: 'Full-stack engineer specializing in biometric systems and real-time data. Previously at a leading fintech startup.' },
  { name: 'Ananya Sharma',  role: 'Customer Success',      avatar: 'AS', color: '#722ed1', bio: 'Onboards and supports 500+ centers. Knows every feature inside out and is always a call away.' },
];

const VALUES = [
  { icon: '🎯', title: 'Simplicity First',   desc: 'Every feature is designed to be intuitive. If your staff needs training for more than 10 minutes, we redesign it.' },
  { icon: '🔒', title: 'Privacy by Design',  desc: 'Biometric data never leaves your server. We are GDPR-aware and handle member data with the highest care.' },
  { icon: '🚀', title: 'Always Improving',   desc: 'We ship updates every two weeks based on real feedback from center owners across India.' },
  { icon: '🤝', title: 'Customer Obsessed',  desc: 'Our support team responds within 2 hours. We treat every center like our only customer.' },
];

const MILESTONES = [
  { year: '2021', title: 'Founded',          desc: 'Started as a simple attendance tracker for a single gym in Mumbai.' },
  { year: '2022', title: 'Biometric Launch', desc: 'Integrated Mantra MFS500 fingerprint scanner for automatic check-ins.' },
  { year: '2023', title: '100 Centers',      desc: 'Reached 100 wellness centers across Maharashtra and Gujarat.' },
  { year: '2024', title: 'Multi-Center',     desc: 'Launched multi-center management, coaches module, and mobile app.' },
  { year: '2025', title: '500+ Centers',     desc: 'Now serving 500+ centers and 50,000+ members across India.' },
];

const About = () => (
  <>
    <style>{`
      .about-hero {
        background: #001529; padding: 100px 32px 80px; text-align: center;
        position: relative; overflow: hidden;
      }
      .about-hero-blob {
        position: absolute; width: 600px; height: 600px; border-radius: 50%;
        background: radial-gradient(circle, rgba(24,144,255,0.12) 0%, transparent 70%);
        top: 50%; left: 50%; transform: translate(-50%,-50%);
        pointer-events: none;
      }
      .about-hero-inner { position: relative; z-index: 1; max-width: 720px; margin: 0 auto; }
      .about-hero-badge {
        display: inline-flex; align-items: center; gap: 7px;
        background: rgba(24,144,255,0.15); border: 1px solid rgba(24,144,255,0.30);
        border-radius: 50px; padding: 6px 14px; margin-bottom: 24px;
        font-size: 12.5px; font-weight: 600; color: #1890ff;
      }
      .about-hero h1 {
        font-size: 48px; font-weight: 900; color: #fff;
        letter-spacing: -1.2px; line-height: 1.1; margin-bottom: 20px;
      }
      .about-hero h1 span { color: #1890ff; }
      .about-hero p {
        font-size: 16px; color: rgba(255,255,255,0.65); line-height: 1.75;
      }

      .pub-section      { padding: 88px 32px; }
      .pub-section-alt  { background: #f8fafc; }
      .pub-section-inner{ max-width: 1200px; margin: 0 auto; }
      .section-eyebrow  { font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #1890ff; margin-bottom: 12px; display: block; }
      .section-title    { font-size: 36px; font-weight: 800; color: #001529; letter-spacing: -0.8px; line-height: 1.2; margin-bottom: 16px; }
      .section-subtitle { font-size: 15px; color: #64748b; line-height: 1.7; max-width: 520px; margin-bottom: 52px; }

      /* Mission split */
      .mission-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: center; }
      .mission-text p { font-size: 15px; color: #475569; line-height: 1.8; margin-bottom: 16px; }
      .mission-stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 8px; }
      .mission-stat {
        background: #001529; border-radius: 14px; padding: 20px;
        text-align: center;
      }
      .mission-stat-val { font-size: 28px; font-weight: 900; color: #1890ff; letter-spacing: -0.5px; }
      .mission-stat-lbl { font-size: 12px; color: rgba(255,255,255,0.55); margin-top: 3px; }

      /* Values */
      .values-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 24px; }
      .value-card {
        background: #fff; border-radius: 16px; padding: 28px;
        border: 1px solid #e8edf2; display: flex; gap: 16px; align-items: flex-start;
        transition: box-shadow .2s;
      }
      .value-card:hover { box-shadow: 0 8px 24px rgba(0,21,41,0.09); }
      .value-icon { font-size: 28px; flex-shrink: 0; }
      .value-card h3 { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
      .value-card p  { font-size: 13.5px; color: #64748b; line-height: 1.65; margin: 0; }

      /* Timeline */
      .timeline { position: relative; padding-left: 32px; }
      .timeline::before {
        content: ''; position: absolute; left: 8px; top: 0; bottom: 0;
        width: 2px; background: #e2e8f0;
      }
      .timeline-item { position: relative; margin-bottom: 36px; }
      .timeline-dot {
        position: absolute; left: -28px; top: 4px;
        width: 16px; height: 16px; border-radius: 50%;
        background: #1890ff; border: 3px solid #fff;
        box-shadow: 0 0 0 2px #1890ff;
      }
      .timeline-year { font-size: 12px; font-weight: 700; color: #1890ff; margin-bottom: 4px; letter-spacing: 0.5px; }
      .timeline-title { font-size: 16px; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
      .timeline-desc  { font-size: 13.5px; color: #64748b; line-height: 1.6; }

      /* Team */
      .team-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 24px; }
      .team-card {
        background: #fff; border-radius: 16px; padding: 28px 20px;
        border: 1px solid #e8edf2; text-align: center;
        transition: box-shadow .2s, transform .2s;
      }
      .team-card:hover { box-shadow: 0 10px 28px rgba(0,21,41,0.10); transform: translateY(-4px); }
      .team-avatar {
        width: 64px; height: 64px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 20px; font-weight: 800; color: #fff;
        margin: 0 auto 14px;
      }
      .team-name { font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 3px; }
      .team-role { font-size: 12px; color: #1890ff; font-weight: 600; margin-bottom: 10px; }
      .team-bio  { font-size: 12.5px; color: #64748b; line-height: 1.6; }

      /* CTA */
      .about-cta { background: #001529; padding: 80px 32px; text-align: center; }
      .about-cta h2 { font-size: 36px; font-weight: 900; color: #fff; letter-spacing: -0.8px; margin-bottom: 14px; }
      .about-cta p  { font-size: 15px; color: rgba(255,255,255,0.60); margin-bottom: 32px; }

      @media (max-width: 1024px) { .team-grid { grid-template-columns: repeat(2,1fr); } }
      @media (max-width: 768px) {
        .mission-grid  { grid-template-columns: 1fr; gap: 40px; }
        .values-grid   { grid-template-columns: 1fr; }
        .team-grid     { grid-template-columns: repeat(2,1fr); }
        .about-hero h1 { font-size: 32px; }
        .section-title { font-size: 26px; }
      }
      @media (max-width: 480px) { .team-grid { grid-template-columns: 1fr; } }
    `}</style>

    {/* Hero */}
    <section className="about-hero">
      <div className="about-hero-blob" />
      <div className="about-hero-inner">
        <div className="about-hero-badge"><HeartFilled /> Our Story</div>
        <h1>We believe <span>wellness</span><br />deserves better tech</h1>
        <p>
          Wellness was born from a simple frustration — managing a
          wellness center was too manual, too slow, and too error-prone.
          We built the platform we wished existed.
        </p>
      </div>
    </section>

    {/* Mission */}
    <section className="pub-section">
      <div className="pub-section-inner">
        <div className="mission-grid">
          <div className="mission-text">
            <span className="section-eyebrow">Our Mission</span>
            <h2 className="section-title">Empowering wellness centers with technology</h2>
            <p>We started Wellness in 2021 with one goal: make running a wellness center as effortless as attending one. Too many great centers were wasting hours on manual attendance registers, paper-based payment records, and missed birthday wishes.</p>
            <p>Today we serve 500+ centers across India — from single-room yoga studios to multi-branch fitness chains — all using Wellness to automate operations and focus on what matters: their members.</p>
            <div style={{ marginTop: 24 }}>
              {['Built in India, for India', 'Dedicated support team', 'Regular feature updates based on your feedback'].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <CheckCircleFilled style={{ color: '#1890ff', fontSize: 16 }} />
                  <span style={{ fontSize: 14, color: '#334155', fontWeight: 500 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mission-stat-grid">
            {[
              { val: '2021', lbl: 'Founded' },
              { val: '500+', lbl: 'Centers' },
              { val: '50K+', lbl: 'Members' },
              { val: '4.9★', lbl: 'Rating' },
            ].map((s, i) => (
              <div key={i} className="mission-stat">
                <div className="mission-stat-val">{s.val}</div>
                <div className="mission-stat-lbl">{s.lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="pub-section pub-section-alt">
      <div className="pub-section-inner">
        <span className="section-eyebrow">Our Values</span>
        <h2 className="section-title">What we stand for</h2>
        <p className="section-subtitle">Four principles guide every decision we make — from product design to customer support.</p>
        <div className="values-grid">
          {VALUES.map((v, i) => (
            <div key={i} className="value-card">
              <span className="value-icon">{v.icon}</span>
              <div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Timeline */}
    <section className="pub-section">
      <div className="pub-section-inner" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' }}>
        <div>
          <span className="section-eyebrow">Our Journey</span>
          <h2 className="section-title">From one gym to 500+ centers</h2>
          <p className="section-subtitle" style={{ marginBottom: 0 }}>Every milestone was driven by real feedback from real center owners.</p>
        </div>
        <div className="timeline">
          {MILESTONES.map((m, i) => (
            <div key={i} className="timeline-item">
              <div className="timeline-dot" />
              <div className="timeline-year">{m.year}</div>
              <div className="timeline-title">{m.title}</div>
              <div className="timeline-desc">{m.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Team */}
    <section className="pub-section pub-section-alt">
      <div className="pub-section-inner">
        <span className="section-eyebrow">The Team</span>
        <h2 className="section-title">The people behind Wellness</h2>
        <p className="section-subtitle">A small, passionate team obsessed with making wellness management effortless.</p>
        <div className="team-grid">
          {TEAM.map((t, i) => (
            <div key={i} className="team-card">
              <div className="team-avatar" style={{ background: t.color }}>{t.avatar}</div>
              <div className="team-name">{t.name}</div>
              <div className="team-role">{t.role}</div>
              <p className="team-bio">{t.bio}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="about-cta">
      <h2>Want to be part of our story?</h2>
      <p>Let's modernize your wellness center together.</p>
      <Button type="primary" size="large" style={{
        borderRadius: 12, background: '#1890ff', borderColor: '#1890ff',
        fontWeight: 700, height: 50, padding: '0 30px', fontSize: 15,
        boxShadow: '0 6px 20px rgba(24,144,255,0.40)'
      }}>
        <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none' }}>
          Get In Touch <ArrowRightOutlined />
        </Link>
      </Button>
    </section>
  </>
);

export default About;
